import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { getFileDownloadUrl } from "../../../../api/firebase/firestoreService";
import { logDataToConsole, logError } from "../../../../utils/customLogger";

interface UseDownloadSftpFileReturn {
  downloadFile: (filePath: string) => Promise<void>;
  isDownloading: boolean;
}

/**
 * Hook to download a file from GCS/Firebase Storage
 * Downloads the file from the given path and triggers browser download
 */
export const useDownloadSftpFile = (): UseDownloadSftpFileReturn => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadFile = useCallback(async (filePath: string): Promise<void> => {
    if (!filePath) {
      toast.error("File path not available");
      return;
    }
    logDataToConsole("Downloading file from SFTP", filePath);

    setIsDownloading(true);

    try {
      // Get the download URL from Firebase Storage
      const downloadUrl = await getFileDownloadUrl(filePath);

      if (!downloadUrl) {
        toast.error("Failed to get download URL");
        return;
      }

      // Fetch the file content
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the file content as blob
      const blob = await response.blob();

      // Extract filename from path
      const filename = filePath.split("/").pop() || "download.csv";

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("File downloaded successfully");
    } catch (error) {
      logError(error as Error, "Error downloading file from SFTP");
      toast.error("Error downloading file");
    } finally {
      setIsDownloading(false);
    }
  }, []);

  return {
    downloadFile,
    isDownloading,
  };
};
